import React, { useState, useEffect } from 'react';
import { 
  Cloud, 
  Plus, 
  Settings, 
  RefreshCw, 
  Trash2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Download,
  Upload,
  Users,
  Calendar,
  HardDrive
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import CountUp from '../../components/CountUp';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import { apiService, CloudConnection, CloudProvider } from '../../services/api';
import type { CloudProvider as CloudProviderType } from '../../services/api/base';
import { authManager } from '../../services/authManager';
import CloudConnectionConfigModal from '../../components/ui/CloudConnectionConfigModal';

interface Integration {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  status: 'connected' | 'disconnected' | 'error';
  accountEmail?: string;
  lastSync?: string | null;
  fileCount?: number;
  storageUsed?: string;
  isConnected: boolean;
  connectionId?: number;
}

const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [connections, setConnections] = useState<CloudConnection[]>([]);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<CloudConnection | null>(null);
  const [totalFilesCount, setTotalFilesCount] = useState<number>(0);
  const { toasts, removeToast, showSuccess, showError } = useToast();

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Créer d'abord la liste des intégrations par défaut (toujours affichée)
      const integrationsList: Integration[] = [
        {
          id: 'google-drive',
          name: 'Google Drive',
          icon: <Cloud className="w-6 h-6 text-blue-500" />,
          description: 'Synchronisez vos documents avec Google Drive',
          status: 'disconnected',
          isConnected: false
        },
        {
          id: 'onedrive',
          name: 'OneDrive',
          icon: <Cloud className="w-6 h-6 text-blue-600" />,
          description: 'Connectez-vous à votre compte OneDrive',
          status: 'disconnected',
          isConnected: false
        },
        {
          id: 'dropbox',
          name: 'Dropbox',
          icon: <Cloud className="w-6 h-6 text-blue-400" />,
          description: 'Synchronisez avec votre Dropbox',
          status: 'disconnected',
          isConnected: false
        },
        {
          id: 'sharepoint',
          name: 'SharePoint',
          icon: <Cloud className="w-6 h-6 text-purple-500" />,
          description: 'Accédez à vos sites SharePoint',
          status: 'disconnected',
          isConnected: false
        }
      ];
      
      // Charger les fournisseurs et connexions en parallèle
      try {
        const [providersData, connectionsData] = await Promise.all([
          apiService.getCloudProviders(),
          apiService.getCloudConnections()
        ]);
        
        setConnections(connectionsData || []);

        // Charger le nombre total de fichiers synchronisés pour toutes les connexions
        let totalFiles = 0;
        if (connectionsData && Array.isArray(connectionsData) && connectionsData.length > 0) {
          try {
            console.log(`📊 Calcul du nombre total de fichiers pour ${connectionsData.length} connexion(s)...`);
            const filesPromises = connectionsData.map(conn => 
              apiService.getCloudFiles(conn.id).catch((err) => {
                console.warn(`⚠️ Erreur lors du chargement des fichiers pour la connexion ${conn.id}:`, err);
                return [];
              })
            );
            const filesArrays = await Promise.all(filesPromises);
            totalFiles = filesArrays.reduce((sum, files) => {
              const count = Array.isArray(files) ? files.length : 0;
              return sum + count;
            }, 0);
            console.log(`✅ Total de ${totalFiles} fichier(s) synchronisé(s) trouvé(s) sur ${connectionsData.length} connexion(s)`);
          } catch (filesError) {
            console.warn('⚠️ Erreur lors du chargement des fichiers pour les statistiques:', filesError);
            totalFiles = 0;
          }
        }
        setTotalFilesCount(totalFiles);

        // Mettre à jour le statut des connexions existantes
        if (connectionsData && Array.isArray(connectionsData)) {
          console.log('🔍 Connexions reçues:', connectionsData);
          
          // Créer de nouveaux objets pour que React détecte le changement
          const updatedIntegrations: Integration[] = integrationsList.map(integration => {
            // Chercher la connexion correspondante (provider_detail ou provider)
            const connection = connectionsData.find(conn => {
              // Gérer les deux formats : provider_detail (objet) ou provider (objet/ID)
              let providerName: string | undefined;
              
              if (conn.provider_detail?.name) {
                providerName = conn.provider_detail.name;
              } else if (typeof conn.provider === 'object' && conn.provider !== null && 'name' in conn.provider) {
                providerName = (conn.provider as CloudProviderType).name;
              }
              
              console.log(`🔍 Comparaison: ${providerName} === ${integration.id}`, providerName === integration.id);
              return providerName === integration.id;
            });
            
            if (connection) {
              console.log(`✅ Connexion trouvée pour ${integration.id}:`, {
                is_active: connection.is_active,
                account_email: connection.account_email,
                last_sync: connection.last_sync
              });
              
              return {
                ...integration,
                status: (connection.is_active ? 'connected' : 'error') as 'connected' | 'disconnected' | 'error',
                isConnected: connection.is_active === true,
                accountEmail: connection.account_email,
                lastSync: connection.last_sync,
                connectionId: connection.id
              };
            } else {
              console.log(`❌ Aucune connexion trouvée pour ${integration.id}`);
            }
            return integration;
          });
          
          console.log('🔍 Intégrations mises à jour:', updatedIntegrations);
          
          // Mettre à jour l'état avec les intégrations modifiées
          setIntegrations(updatedIntegrations);
        } else {
          // Si pas de connexions, afficher les intégrations par défaut
          setIntegrations(integrationsList);
        }
      } catch (apiError) {
        console.warn('Erreur lors du chargement des données API (non bloquant):', apiError);
        // Afficher les intégrations par défaut même en cas d'erreur
        setIntegrations(integrationsList);
      }
      
    } catch (err) {
      console.error('Erreur lors du chargement des données:', err);
      showError('Erreur de chargement', 'Impossible de charger les intégrations cloud');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (integrationId: string) => {
    try {
      // Vérifier que l'utilisateur est authentifié
      const currentUser = authManager.getUser();
      if (!currentUser || !currentUser.id) {
        showError('Authentification requise', 'Veuillez vous connecter pour utiliser cette fonctionnalité.');
        return;
      }
      
      // Obtenir l'URL d'authentification
      // Le backend inclura automatiquement l'ID utilisateur dans le state
      const authData = await apiService.getAuthUrl(integrationId);
      
      // Pour Google Drive, rediriger directement vers l'URL OAuth
      // Le callback sera géré par le backend qui redirigera vers le frontend
      if (integrationId === 'google-drive') {
        // Stocker le provider dans sessionStorage pour le callback
        sessionStorage.setItem('oauth_provider', integrationId);
        sessionStorage.setItem('oauth_state', authData.state || '');
        
        // Rediriger vers l'URL OAuth
        window.location.href = authData.auth_url;
        return;
      }
      
      // Pour les autres providers, utiliser une fenêtre popup
      const authWindow = window.open(
        authData.auth_url,
        'oauth_auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      // Écouter les messages de la fenêtre d'authentification
      const handleMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'OAUTH_SUCCESS') {
          try {
            // Gérer le callback OAuth
            await apiService.handleOAuthCallback(event.data.code, event.data.state, integrationId);
            
            // Recharger les données
            await loadData();
            
            showSuccess(
              'Connexion réussie',
              `Votre compte ${integrationId} a été connecté avec succès`
            );
            
            // Fermer la fenêtre d'authentification
            if (authWindow) {
              authWindow.close();
            }
            
            // Nettoyer l'écouteur
            window.removeEventListener('message', handleMessage);
            
          } catch (err) {
            console.error('Erreur lors de la connexion:', err);
            showError('Erreur de connexion', 'Impossible de finaliser la connexion');
          }
        }
      };

      window.addEventListener('message', handleMessage);
      
    } catch (err: any) {
      console.error('Erreur lors de l\'initiation de la connexion:', err);
      
      // Extraire le message d'erreur détaillé
      let errorMessage = 'Impossible d\'initier la connexion';
      let errorTitle = 'Erreur de connexion';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        errorMessage = errorData.message || errorData.error || errorData.details || errorMessage;
        errorTitle = errorData.error || errorTitle;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      // Message spécial pour les credentials manquantes
      if (errorMessage.includes('credentials') || errorMessage.includes('Configuration') || errorMessage.includes('configurées')) {
        errorMessage = 'Les credentials Google Drive ne sont pas configurées. Veuillez contacter l\'administrateur pour configurer l\'intégration.';
        errorTitle = 'Configuration manquante';
      }
      
      showError(errorTitle, errorMessage);
    }
  };

  const handleDisconnect = async (integrationId: string) => {
    const integration = integrations.find(integ => integ.id === integrationId);
    if (!integration?.connectionId) return;

    if (window.confirm(`Êtes-vous sûr de vouloir déconnecter ${integration.name} ?`)) {
      try {
        await apiService.deleteCloudConnection(integration.connectionId);
        
        // Recharger les données
        await loadData();
        
        showSuccess(
          'Déconnexion réussie',
          `Votre compte ${integration.name} a été déconnecté`
        );
        
      } catch (err) {
        console.error('Erreur lors de la déconnexion:', err);
        showError('Erreur de déconnexion', 'Impossible de déconnecter le compte');
      }
    }
  };

  const handleSync = async (integrationId: string) => {
    const integration = integrations.find(integ => integ.id === integrationId);
    if (!integration?.connectionId) {
      showError('Erreur', 'Connexion introuvable');
      return;
    }

    // Pour Google Drive, vérifier si des dossiers sont configurés
    if (integration.id === 'google-drive') {
      const connection = connections.find(c => c.id === integration.connectionId);
      const syncFolders = connection?.sync_folders || [];
      
      // Si aucun dossier n'est configuré, proposer de configurer d'abord
      if (syncFolders.length === 0) {
        const shouldConfigure = window.confirm(
          '⚠️ Aucun dossier sélectionné\n\n' +
          'Souhaitez-vous configurer les dossiers à synchroniser avant de lancer la synchronisation ?\n\n' +
          'Si vous synchronisez tout le Drive, cela peut prendre beaucoup de temps.\n\n' +
          'Cliquez sur "OK" pour configurer les dossiers, ou "Annuler" pour synchroniser tout le Drive.'
        );
        
        if (shouldConfigure) {
          // Ouvrir la modal de configuration
          if (connection) {
            setSelectedConnection(connection);
            setConfigModalOpen(true);
          }
          return;
        }
      }
    }

    try {
      setSyncing(integrationId);
      
      // Afficher un message immédiat pour indiquer que la synchronisation démarre
      const connection = connections.find(c => c.id === integration.connectionId);
      const syncFolders = connection?.sync_folders || [];
      
      let syncMessage = 'La synchronisation est en cours. Cela peut prendre quelques minutes...';
      if (integration.id === 'google-drive' && syncFolders.length > 0) {
        syncMessage = `Synchronisation de ${syncFolders.length} dossier(s) sélectionné(s). Cela peut prendre quelques minutes...`;
      } else if (integration.id === 'google-drive') {
        syncMessage = '⚠️ Synchronisation de TOUS les dossiers. Cela peut prendre beaucoup de temps...';
      }
      
      showSuccess(
        'Synchronisation démarrée',
        syncMessage
      );
      
      console.log(`🔄 Démarrage de la synchronisation pour ${integration.name} (ID: ${integration.connectionId})`);
      
      const result = await apiService.syncCloudFiles(integration.connectionId);
      
      console.log('📊 Résultat de la synchronisation:', result);
      
      if (result.success) {
        const syncedCount = result.result?.synced_files || result.result?.synced_count || 0;
        const indexedCount = result.result?.indexed_files || 0;
        
        let message = `${syncedCount} fichier(s) synchronisé(s)`;
        if (indexedCount > 0) {
          message += `, ${indexedCount} indexé(s) avec RAG`;
        }
        
        showSuccess(
          'Synchronisation réussie',
          message
        );
        
        // Recharger les données pour mettre à jour les statistiques
        await loadData();
      } else {
        const errorMsg = result.result?.error || result.result?.message || 'La synchronisation a échoué';
        console.error('❌ Erreur de synchronisation:', errorMsg);
        showError('Erreur de synchronisation', errorMsg);
      }
      
    } catch (err: any) {
      console.error('❌ Erreur lors de la synchronisation:', err);
      console.error('❌ Détails de l\'erreur:', {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      
      let errorMessage = 'Impossible de synchroniser les fichiers';
      if (err?.response?.data) {
        errorMessage = err.response.data.error || err.response.data.message || errorMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      showError('Erreur de synchronisation', errorMessage);
    } finally {
      setSyncing(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-eg-valide bg-eg-valide/10 border-green-200';
      case 'error':
        return 'text-eg-accent bg-eg-accent/10 border-red-200';
      default:
        return 'text-eg-gris bg-eg-papier border-eg-bord';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="w-4 h-4" />;
      case 'error':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-eg-accent"></div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-eg-encre">Intégrations Cloud</h1>
          <p className="text-eg-gris mt-2">
            Connectez et synchronisez vos services de stockage cloud
          </p>
        </div>
        <Button
          size="lg"
          icon={<RefreshCw className="w-5 h-5" />}
          onClick={loadData}
        >
          Actualiser
        </Button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-eg-voile rounded-md">
              <Cloud className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-eg-gris">Services connectés</p>
              <p className="text-2xl font-bold text-eg-encre">
                <CountUp 
                  to={connections.filter(c => c.is_active).length} 
                  duration={1.5} 
                  delay={0.2} 
                  className="text-2xl font-bold text-eg-encre" 
                />
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-eg-valide/10 rounded-md">
              <Download className="w-6 h-6 text-eg-valide" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-eg-gris">Fichiers synchronisés</p>
              <p className="text-2xl font-bold text-eg-encre">
                <CountUp 
                  to={totalFilesCount} 
                  duration={1.5} 
                  delay={0.4} 
                  className="text-2xl font-bold text-eg-encre" 
                />
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-md">
              <Upload className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-eg-gris">Dernière sync</p>
              <p className="text-2xl font-bold text-eg-encre">
                {connections.some(c => c.last_sync) ? '✓' : '—'}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-md">
              <HardDrive className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-eg-gris">Stockage total</p>
              <p className="text-2xl font-bold text-eg-encre">—</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Liste des intégrations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {integrations.map((integration) => (
          <Card key={integration.id} className="relative">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-eg-voile rounded-md">
                  {integration.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-lg font-semibold text-eg-encre">
                      {integration.name}
                    </h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(integration.status)}`}>
                      {getStatusIcon(integration.status)}
                      <span className="ml-1">
                        {integration.status === 'connected' ? 'Connecté' : 
                         integration.status === 'error' ? 'Erreur' : 'Non connecté'}
                      </span>
                    </span>
                  </div>
                  
                  <p className="text-eg-gris mt-1">{integration.description}</p>
                  
                  {integration.isConnected && (
                    <div className="mt-3 space-y-2">
                      {integration.accountEmail && (
                        <div className="flex items-center text-sm text-eg-gris">
                          <Users className="w-4 h-4 mr-2" />
                          {integration.accountEmail}
                        </div>
                      )}
                      {integration.lastSync && (
                        <div className="flex items-center text-sm text-eg-gris">
                          <Calendar className="w-4 h-4 mr-2" />
                          Dernière sync: {new Date(integration.lastSync).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center space-x-3">
              {integration.isConnected ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<RefreshCw className={`w-4 h-4 ${syncing === integration.id ? 'animate-spin' : ''}`} />}
                    onClick={() => handleSync(integration.id)}
                    disabled={syncing === integration.id}
                    className="px-2"
                  >
                    {syncing === integration.id ? 'Synchronisation...' : 'Synchroniser'}
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Settings className="w-4 h-4" />}
                    onClick={async () => {
                      // Recharger les connexions pour avoir les données à jour
                      try {
                        const updatedConnections = await apiService.getCloudConnections();
                        setConnections(updatedConnections || []);
                        
                        const connection = (updatedConnections || []).find(c => c.id === integration.connectionId) || 
                                         connections.find(c => c.id === integration.connectionId);
                        if (connection) {
                          console.log('🔧 Ouverture de la modal de configuration pour:', connection);
                          console.log('🔧 Provider:', connection.provider_detail || connection.provider);
                          setSelectedConnection(connection);
                          setConfigModalOpen(true);
                        } else {
                          showError('Erreur', 'Connexion introuvable');
                        }
                      } catch (err) {
                        console.error('Erreur lors du rechargement des connexions:', err);
                        // Essayer quand même avec les connexions existantes
                        const connection = connections.find(c => c.id === integration.connectionId);
                        if (connection) {
                          setSelectedConnection(connection);
                          setConfigModalOpen(true);
                        }
                      }
                    }}
                    className="px-2"
                  >
                    Configurer
                  </Button>
                  
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<Trash2 className="w-4 h-4" />}
                    onClick={() => handleDisconnect(integration.id)}
                    className="px-2"
                  >
                    Déconnecter
                  </Button>
                </>
              ) : (
                // Vérifier si l'intégration est disponible (seulement Google Drive pour l'instant)
                integration.id === 'google-drive' ? (
                  <Button
                    size="sm"
                    icon={<Plus className="w-4 h-4" />}
                    onClick={() => handleConnect(integration.id)}
                  >
                    Se connecter
                  </Button>
                ) : (
                  <div className="flex items-center space-x-2 px-4 py-2 bg-eg-voile rounded-md border border-[#DDE2DD]">
                    <span className="text-sm font-medium text-eg-gris">⏳ Prochainement disponible</span>
                  </div>
                )
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Section d'aide */}
      <Card className="bg-teal-50 border-teal-200">
        <div className="flex items-start space-x-4">
          <div className="p-2 bg-teal-100 rounded-md">
            <AlertCircle className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-teal-900">
              Comment fonctionne la synchronisation ?
            </h3>
            <div className="text-teal-700 mt-2 space-y-3">
              <p>
                Connectez vos services de stockage cloud pour accéder à vos documents directement depuis la plateforme. 
                Vos fichiers seront synchronisés et rendus accessibles via la recherche intelligente.
              </p>
              <div className="space-y-2">
                <p className="font-medium">📋 Comment ça marche ?</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Connectez-vous à votre service de stockage cloud</li>
                  <li>Choisissez les dossiers que vous souhaitez synchroniser</li>
                  <li>Lancez la synchronisation pour importer vos fichiers</li>
                  <li>Recherchez et accédez à vos documents via la recherche intelligente</li>
                </ol>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-teal-600">
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Choisissez vos dossiers
                </span>
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Synchronisation automatique
                </span>
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Recherche intelligente
                </span>
                <span className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Tous vos formats de documents
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Modal de configuration */}
      <CloudConnectionConfigModal
        isOpen={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedConnection(null);
        }}
        connection={selectedConnection}
      />
    </DashboardLayout>
  );
};

export default IntegrationsPage;
